import { default as React, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { Paper, Grid } from '@mui/material'
import Page from '../../layout/Page.js'
import { useSearchParams } from 'react-router-dom'
import RoleContext from '../../auth/RoleContext.js';
import { ROLES } from '../../auth/role.js';
import EventBus from '../../utils/EventBus.js';
import FeaturedArticles from './FeaturedArticles.js'
import NonFeaturedArticles from './NonFeaturedArticles.js'
import '../CommunityNewsPage.css'
import Event from '../../utils/EventNames.js';
import { AppliedFilters, TAG_QUERY_PARAM } from '../Tags.js'
import TabPanel from '../../form/TabPanel.js'
import { listArticles, getArticlesByTags, getListArticlesPageFromCache } from '../data/index.js'
import ArticlesCommandBar from '../ArticleComponents/ArticlesCommandBar.js'
import AddEditArticleDialog from '../ArticleComponents/AddEditArticleDialog.js'
import CommunityEventNames from '../EventNames.js'
import Cache from '../../cache/Cache.js';
import ShareArticleDialog from '../ArticleComponents/ShareArticleDialog.js';
import SharePreviewDialog from '../ArticleComponents/SharePreviewDialog.js';
import { getSiteConfig } from '../../community/siteProvider.js';

const TabbedViews = {
  PUBLISHED: 'published',
  UNPUBLISHED: 'unpublished'
}


const initialState = {
  page: {
    currentView: TabbedViews.PUBLISHED,
    loading: false,
  },

  unpublished: {
    featured: {
      loading: false,
      loadingMore: false,
      articles: undefined,
      totalAvailable: undefined,
      totalLoaded: 0,
      nextToken: undefined,
    },

    nonFeatured: {
      loading: false,
      loadingMore: false,
      articles: undefined,
      totalAvailable: undefined,
      totalLoaded: 0,
      nextToken: undefined,
    },
  },

  published: {
    featured: {
      loading: false,
      loadingMore: false,
      articles: undefined,
      totalAvailable: undefined,
      totalLoaded: 0,
      nextToken: undefined,
    },

    nonFeatured: {
      loading: false,
      loadingMore: false,
      articles: undefined,
      totalAvailable: undefined,
      totalLoaded: 0,
      nextToken: undefined,
    },
  }

}

function articleReducer(state, action) {
  const _state = structuredClone(state)
  if(action === 'resetArticles') {
    _state.unpublished = initialState.unpublished
    _state.published = initialState.published
    return _state
  }
  const [currentView, type, key, value] = action
  if(currentView) {
    _state[currentView][type][key] = value
  } else {
    _state[type][key] = value
  }

  return _state
}

function InnerCommunityNewsPage(props = {resetPage: undefined, siteConfig: undefined}) {
  const roleDetails = useContext(RoleContext)

  const canEdit = roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.ADMIN])
  const [newsMap, dispatchNews] = useReducer(articleReducer, initialState)
  const [searchParams, setSearchParams] = useSearchParams();

  const currentView = newsMap.page.currentView
  const filter = searchParams.get(TAG_QUERY_PARAM)
  
  const update = (articlesResponse, _currentView, type, forceReset) => {
    const { articles: _articles, totalSize, nextLink, totalLoaded } = articlesResponse
    if(_articles) {
      const prevArticles = forceReset ? [] : [...(newsMap[_currentView][type].articles || [])]
      const newArticles = [...prevArticles, ..._articles]
      dispatchNews([_currentView, type, 'articles', newArticles])
      dispatchNews([_currentView, type, 'totalAvailable', totalSize])
      dispatchNews([_currentView, type, 'nextToken', nextLink])
      if(totalLoaded) dispatchNews([_currentView, type, 'totalLoaded', totalLoaded])
    }
  }

  const loadMoreArticles = async (forceReset = false, type, limit, _currentView, _searchParams) => {
    if(forceReset) {
      dispatchNews([_currentView, type, 'articles', []])
      dispatchNews([_currentView, type, 'totalAvailable', undefined])
      dispatchNews([_currentView, type, 'nextToken', undefined])
      dispatchNews([_currentView, type, 'totalLoaded', 0])
      dispatchNews([_currentView, type, 'loading', true])
    }
    else dispatchNews([_currentView, type, 'loadingMore', true])

    const tags = _searchParams?.get(TAG_QUERY_PARAM)?.split(',');
    const offset = forceReset ? 0 : newsMap[_currentView][type].articles?.length || 0
    const visibility = { news: true, featured: type === 'featured' }
    const published = _currentView === 'published'
    const nextToken = newsMap[_currentView][type].articles?.nextToken

    if(tags?.length) {
      for await (const articlesResponse of getArticlesByTags({visibility, tags, startIndex: offset, limit, nextToken})) {
        update(articlesResponse, _currentView, type, forceReset)
      }
    } else {
      for await (const articlesResponse of listArticles(
        visibility,
        published,
        offset,
        newsMap[_currentView][type].nextToken,
        limit)
      ) {
        update(articlesResponse, _currentView, type, forceReset)
      }

      dispatchNews([_currentView, type, 'loadingMore', false])
    }

    dispatchNews([_currentView, type, 'loading', false])
  }

  const loadCached = (type, _newsMap, _currentView) => {
    const item =  _newsMap[_currentView][type]
    if(item?.totalLoaded || 0 < item?.totalAvailable) {
      const cacheHit = getListArticlesPageFromCache(
        {news: true, featured: false},
        true,
        (item?.articles?.length || 0),
        item?.nextLink,
        4
      );
      if(cacheHit) {
        update(cacheHit, _currentView, type, false)
      }
    }
  }

  useEffect(() => {
    loadCached('featured', newsMap, currentView)
    loadCached('nonFeatured', newsMap, currentView)
  }, [newsMap, currentView])

  const FeaturedArticlesLayout = <Grid container gap={0}>
    {filter && <AppliedFilters
      clickable
      sx={{ paddingLeft: '4px' }}
    />}
    <Grid item xs={12} sx={{ padding: '4px', flexGrow: '1' }}>
      <FeaturedArticles
        editable={canEdit}
        featured
        news
        published={newsMap.page.currentView === 'published'}
        articles={newsMap[currentView].featured.articles}
        loading={newsMap[currentView].featured.loading}
        loadingMore={newsMap[currentView].featured.loadingMore}
        totalArticlesAvailable={newsMap[currentView].featured.totalAvailable}
        moreAvailable={newsMap[currentView].featured.totalAvailable > newsMap[currentView].featured.articles?.length || newsMap[currentView].featured.nextToken !== undefined}
        loadMoreArticles={() => loadMoreArticles(false, 'featured', 3, currentView, searchParams)}
      />
    </Grid>
    <Grid item xs={12} sx={{ flexGrow: '2', padding: '4px' }}>
      <NonFeaturedArticles
        editable={canEdit}
        news
        published={newsMap.page.currentView !== 'unpublished'}
        infiniteScroll
        articles={newsMap[currentView].nonFeatured.articles}
        loading={newsMap[currentView].nonFeatured.loading}
        loadingMore={newsMap[currentView].nonFeatured.loadingMore}
        totalArticlesAvailable={newsMap[currentView].nonFeatured.totalAvailable}
        moreAvailable={newsMap[currentView].nonFeatured.totalAvailable > newsMap[currentView].nonFeatured.articles?.length || newsMap[currentView].nonFeatured.nextToken !== undefined}
        loadArticles={() => loadMoreArticles(false, 'nonFeatured', 4, currentView, searchParams)}
      />
    </Grid>
  </Grid>

  const tabViews = [
    {
      key: TabbedViews.PUBLISHED,
      label: 'Published',
      el: FeaturedArticlesLayout
    },
    {
      key: TabbedViews.UNPUBLISHED,
      label: 'Unpublished',
      el: FeaturedArticlesLayout
    },
  ]

  const onTabChange = (tabIndex) => {
    const viewKey = tabViews[tabIndex].key
    dispatchNews([null, 'page', 'currentView', viewKey])
  }

  const startLoading = () => {
    dispatchNews([null, 'page', 'loading', true])
  }

  const stopLoading = () => {
    dispatchNews([null, 'page', 'loading', false])
  }

  useEffect(() => {
    const view = searchParams.get('view')
    if (view) {
      dispatchNews([null, 'page', 'currentView', true])
    }
  }, [searchParams])

  useEffect(() => {
    if(props.resetPage) {
      EventBus.on(Event.REFRESH_PAGE, props.resetPage)
      return () => {
        EventBus.removeListener(Event.REFRESH_PAGE, props.resetPage)
      }
    }
  }, [props.resetPage])

  useEffect(() => {
    loadMoreArticles(true, 'featured', 3, currentView, searchParams)
    loadMoreArticles(true, 'nonFeatured', 4, currentView, searchParams)
  }, [currentView, filter])

  useEffect(() => {
    EventBus.on(Event.START_PAGE_LOADING, startLoading)
    EventBus.on(Event.STOP_PAGE_LOADING, stopLoading)
    return () => {
      EventBus.removeListener(Event.START_PAGE_LOADING, startLoading)
      EventBus.removeListener(Event.STOP_PAGE_LOADING, stopLoading)
    }
  }, [])

  const addArticle = () => {
    EventBus.emit(CommunityEventNames.OPEN_ADD_EDIT_ARITCLE_DIALOG, {
      mode: 'add',
      article: {
        news: true
      },
      visibility: {
        news: true
      }
    })
  }

  const defaultTab = tabViews.indexOf(tabViews.find(tv => tv.key === searchParams.get('view')))
  return (
    <Page {...props} breadcrumb={<div><span>{props.siteConfig?.label ?? 'Community'} News</span></div>} isLoading={newsMap.page.loading}
      navClassName='article-nav'
      commandBar={<ArticlesCommandBar
        addRecordLabel='Create an Article'
        showAdd={canEdit}
        addRecord={addArticle}
        addRecordButtonVariant="contained"
      />}
    >

      <Paper
        component="form"
        sx={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', margin: 'a auto 1em auto', paddingBottom: '20px' }}
      >

        {canEdit && <AddEditArticleDialog />}
        <ShareArticleDialog/>
        <SharePreviewDialog/>

        {roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.ADMIN]) && <TabPanel fullWidth panelId='articles' key='articles' tabs={tabViews}
          defaultTab={searchParams.get('view') ? defaultTab === -1 ? 0 : defaultTab : 0}
          onTabChange={(idx) => onTabChange(idx)}
          tabStyle={{ textTransform: 'none', minHeight: 'unset'}}
        />}
        {!roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.ADMIN]) && tabViews[0].el}
      </Paper>
    </Page>
  )
}

// It's easier to just completely reset the component and blow away cache keys here to ensure the cache context always gets updated
export default function CommunityNewsPage(props) {
  const { pathname } = useLocation()

  const siteKey = useMemo(() => {
    return pathname.split('/').filter(Boolean)[0] ?? null
  }, [pathname])

  const [config, setConfig] = useState(null)

  useEffect(() => {
    if (!siteKey) return;

    resetPage()
    const _config = getSiteConfig(siteKey)
    setConfig(_config)
  }, [siteKey])

  const [instanceKey, setInstanceKey] = React.useState(0);
  // call this anywhere to reset the component
  const resetPage = () => {
    Cache.removeItem('site-settings')
    Cache.removeItemsStartingWith('pinned-articles')
    Cache.removeItemsStartingWith('news-totalLoaded')
    Cache.removeItemsStartingWith('get-articles')
    Cache.removeItemsStartingWith('list-articles')
    const oldKey = instanceKey
    const newKey = oldKey + 1
    setInstanceKey(newKey);
  };

  // render an inner "real" version of the component with a key
  return (
    <InnerCommunityNewsPage
      key={instanceKey}
      siteConfig={config}
      {...props}
      resetPage={resetPage}
    />
  );
}